import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '../../services/toast/toast.service';
import { LoaderService } from '../../services/loader/loader.service';
import { lastValueFrom } from 'rxjs';
import { WorkOrderService } from '../../services/work-order/work-order.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LocalStorageService } from '../../services/local-storage/localstorage.service';
import { CreateChildWO, WorkOrderTypeItem } from '../../models/work-order.model';
import { UserPreferencesData } from '../../models/user-preferences.model';
import { MatDialogRef } from '@angular/material/dialog'; // Add this import at the top

@Component({
  selector: 'app-create-child-wo-popup',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-36/COL-37), out of scope for this hotfix
  standalone: false,
  templateUrl: './create-child-wo-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-child-wo-popup.component.scss',
})
export class CreateChildWOPopupComponent implements OnInit {
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private loaderService = inject(LoaderService);
  private workOrderService = inject(WorkOrderService);
  private localStorageService = inject(LocalStorageService);
  private dialogRef = inject<MatDialogRef<CreateChildWOPopupComponent>>(MatDialogRef);
  data = inject<{
    workOrderData;
}>(MAT_DIALOG_DATA);

  childWorkOrderForm: FormGroup;
  workOrderTypeList: WorkOrderTypeItem[] = [];
  /** True once the child types have loaded, so an empty list can say why the dropdown is empty. */
  workOrderTypesLoaded = false;
  WOCode = '';

  constructor() {
    this.createChildWoForm();
  }

  async ngOnInit() {
    if (this.data?.workOrderData?.[0].id) {
      await this.getWorkOrderTypes(this.data?.workOrderData?.[0].id);
      this.WOCode = this.data?.workOrderData?.[0].woCode;
    }
    this.updateReferenceNames(1);
  }

  createChildWoForm() {
    this.childWorkOrderForm = this.fb.group({
      workOrderType: ['', Validators.required],
      numOfChildWorkOrders: [
        1,
        [Validators.required, Validators.min(1), Validators.max(25)],
      ],
      referenceNames: this.fb.array([
        this.fb.control('', /*[
          Validators.required,
          Validators.pattern(/^(?!\s*$).+/),
        ]*/),
      ]),
    });
  }

  generateChildWoRefInputs() {
    const value = this.childWorkOrderForm.get('numOfChildWorkOrders').value;

    if (value > 25) {
      // Reset value to 1 if it exceeds 25
      // this.childWorkOrderForm.get('numOfChildWorkOrders').setValue(1);
      // this.updateReferenceNames(1);
      // Show toast message
      this.toastService.showError(
        'Maximum number of Child Work Orders allowed is 25.'
      );
    } else if (value !== this.referenceNames.length) {
      this.updateReferenceNames(value);
    }
  }

  // Getter for referenceNames FormArray
  get referenceNames(): FormArray {
    return this.childWorkOrderForm.get('referenceNames') as FormArray;
  }

  // Update the referenceNames FormArray based on the specified number
  updateReferenceNames(count: number) {
    this.referenceNames.clear();
    for (let i = 0; i < count; i++) {
      this.referenceNames.push(this.fb.control('', /*[
        Validators.required,
        Validators.pattern(/^(?!\s*$).+/)
      ]*/));
    }
  }

  addNewRows() {
    const value = this.childWorkOrderForm.get('numOfChildWorkOrders').value;

    if (value >= 25) {
      this.toastService.showError(
        'Maximum number of Child Work Orders allowed is 25.'
      );
      return;
    }
    this.referenceNames.push(this.fb.control('', /*[Validators.required, Validators.pattern(/^(?!\s*$).+/)]*/));
    this.childWorkOrderForm
        .get('numOfChildWorkOrders')
        .setValue(this.referenceNames.length);
  }

  // Remove a reference name control at a specific index
  removeReferenceName(index: number) {
    if (this.referenceNames.length > 1) {
      // Remove the control at the specified index
      this.referenceNames.removeAt(index);

      // Update numOfChildWorkOrders to reflect the updated length of referenceNames
      this.childWorkOrderForm
        .get('numOfChildWorkOrders')
        .setValue(this.referenceNames.length);
    } else {
      this.toastService.showError('At least one reference name is required.');
    }
  }

  // Handle dynamic updates based on the first input value
  onReferenceNameChange(index: number) {
    const referenceNameControl = this.referenceNames.at(index);
    const currentValue = referenceNameControl.value;

    const baseName = currentValue.replace(/\d+$/, '');
    const startNumber = parseInt(currentValue.replace(/\D+/g, ''), 10) || 1; // Start from 1 if no number is found

    for (let i = index + 1; i < this.referenceNames.length; i++) {
      const control = this.referenceNames.at(i);
      control.setValue(`${baseName} ${startNumber + (i - index)}`);
    }
  }

  async getWorkOrderTypes(id) {
    const workOrderId = id;
    if (!workOrderId) {
      console.warn(
        'Work order ID is missing. Unable to fetch work order types.'
      );
      this.workOrderTypeList = []; // Clear the list if there's no valid ID
      return;
    }

    try {
      await this.loaderService.showLoader();

      const response: WorkOrderTypeItem[] = await lastValueFrom(
        this.workOrderService.getChildWOTypeByWOId(workOrderId)
      );

      await this.loaderService.hideLoader();
      this.workOrderTypeList = response || [];
      this.workOrderTypesLoaded = true;
    } catch (error) {
      await this.loaderService.hideLoader();
      console.error('Error fetching work order types:', error);
    }
  }

  onSave() {
    if (this.childWorkOrderForm.invalid || this.childWorkOrderForm.pristine) {
      this.childWorkOrderForm.markAllAsTouched();
      this.toastService.showError('Please fill in all required fields.');
      return;
    }
    const formData = this.childWorkOrderForm.value;
    const referenceNamesValues = formData.referenceNames;
    if (!referenceNamesValues.length) { 
      return;
    }

    const userPref = this.localStorageService.getItem<UserPreferencesData>('userPreferences');


    const payload: CreateChildWO = {
      parentWorkOrderId: this.data?.workOrderData?.[0].id,
      workOrderTypeId: formData.workOrderType,
      projectId: userPref?.selectedProjectId || 0,
      userId: userPref?.userId || 0,
      companyId: userPref?.selectedCompanyId || 0,
      refNameArray: referenceNamesValues,
      fields: {}
    }
    this.loaderService.showLoader();

    // Then in the onSave method, modify the subscription:
    this.workOrderService.createChildWorkOrder(payload).subscribe({
      next: (response) => {
        this.loaderService.hideLoader();
        this.toastService.showSuccess('Child Work Order created successfully.');
        this.dialogRef.close(response); // Close dialog and pass response data back
      },
      error: () => {
        this.loaderService.hideLoader();
        this.toastService.showError('Error creating child work order.');
      },
    });
  }
}
